import React, { useRef, useState, useEffect } from 'react'
import { flushSync } from 'react-dom'
import i18next from 'i18next'
import DxDataTable from 'components/DxDataTable'
import { Template } from 'devextreme-react/core/template'
import { Editing } from 'devextreme-react/data-grid'
import { Button as DxButton } from 'devextreme-react/button'
import LoadPanel from 'devextreme-react/load-panel'
import readXlsxFile from 'read-excel-file'
import styled from 'styled-components'
import { useHistory } from 'react-router-dom'
import createXLS from 'helpers/fileGenerators/xlsGenerator'

const StyledDxDataTable = styled(DxDataTable)`
	max-height: calc(100vh - 144px);
	.dx-focused {
		overflow: visible !important;
	}
	tr {
		height: 36px;
	}
	td {
		padding: 0 7px !important;
	}
`

const GridView = ({ sectionPath, onImported, selectedRows, exportData, onMultiEditEnabled, ...props }) => {
	const history = useHistory()
	const importedData = useRef()
	const lastAddInd = useRef(0)
	const tableRef = useRef()
	const fileInputRef = useRef()

	const [isImporting, setIsImporting] = useState(false)

	const handleUpload = e => {
		const file = e.target.files[0]
		fileInputRef.current.value = ''
		lastAddInd.current = 0
		flushSync(() => setIsImporting(true))
		readXlsxFile(file)
			.then(data => {
				if(Array.isArray(data)) {
					const langs = Object.keys(i18next.options?.resources || {})
					const colsMap = props?.columns?.reduce?.((outObj, item) => {
						outObj[item.caption.toLowerCase()] = item.dataField
						if(item.captionKey) {
							langs.forEach(lang => {
								const translated = i18next.getFixedT(lang)(item.captionKey)
								if(translated && translated !== item.captionKey) {
									outObj[translated.toLowerCase()] = item.dataField
								}
							})
						}
						return outObj
					}, {})
					const [fileCols, ...rows] = data
					if(Array.isArray(fileCols) && Array.isArray(rows)) {
						const tableData = rows.reduce((outArr, row) => {
							const parsedRow = {}
							row.forEach((item, i) => {
								const colName = fileCols?.[i]?.trim()?.toLowerCase()
								const caption = colsMap[colName]
								parsedRow[caption] = item
							})
							outArr.push(parsedRow)
							return outArr
						}, [])
						const dataGridInst = tableRef?.current?.instance
						importedData.current = tableData.reverse()

						return new Promise(resolve => {
							const batchSize = 10
							let added = 0
							const total = importedData.current.length

							function addBatch() {
								const end = Math.min(added + batchSize, total)
								while (added < end) {
									dataGridInst.addRow()
									added++
								}
								if (added < total) {
									setTimeout(addBatch, 0)
								} else {
									if (typeof onImported === 'function') onImported()
									resolve()
								}
							}

							setTimeout(addBatch, 0)
						})
					}
				}
			})
			.finally(() => {
				setIsImporting(false)
			})
	}

	const [selectedData, setSelectedData] = useState([])
	const [isMultiAddEnabled, setIsMultiAddEnabled] = useState(false)

	useEffect(() => {
		if (selectedRows && selectedRows.length) {
			setSelectedData(props.data.filter(item => selectedRows.includes(item.id)))
		}
	}, [props.data, selectedRows])

	const multiAddRender = () => {
		return(
			<DxButton
				type={ isMultiAddEnabled ? 'default' : 'normal' }
				icon='rename'
				onClick={() => {
					if (!isMultiAddEnabled) {
						tableRef.current.instance.addRow()
					}
					setIsMultiAddEnabled(prevValue => {
						onMultiEditEnabled(!prevValue)
						return !prevValue
					})
				}}
			/>
		)
	}

	const importRender = () => {
		return(
			<label>
				<input ref={fileInputRef} hidden type='file' onChange={handleUpload} accept='.xlsx' multiple={false} />
				<DxButton icon='inserttable' />
			</label>
		)
	}

	const exportRender = () => {
		return(
			<DxButton icon='xlsxfile' onClick={exportXLSX} disabled={!selectedData.length} />
		)
	}

	const exportXLSX = () => {
		createXLS({
			...exportData,
			selectedData
		})
	}

	const handleAdd = e => {
		if(importedData.current) {
			e.data = importedData.current[lastAddInd.current]
			lastAddInd.current++ // eslint-disable-line no-plusplus
			if(lastAddInd.current === importedData.current?.length) {
				importedData.current = null
			}
		}
	}

	const MultiAddTemplate = <Template key='multiAdd' name='multiAdd' render={multiAddRender} />
	const ImportTemplate = <Template key='import' name='import' render={importRender} />
	const ExportTemplate = <Template key='export' name='export' render={exportRender} />

	return(
		<>
		<LoadPanel
			visible={isImporting}
			showIndicator={true}
			showPane={true}
			shading={true}
			shadingColor='rgba(0,0,0,0.4)'
			message={i18next.t('menu_card.upload_files_process')}
		/>
		<StyledDxDataTable
			{...props}
			getRef={ref => {
				tableRef.current = ref
			}}
			withSearch
			columnAutoWidth={false}
			remoteOperations={true}
			repaintChangesOnly={true}
			onKeyDown={async ({ event, ...rest }) => {
				if(event.target.id.includes('react-select') && (
					event.key === 'ArrowUp' ||
					event.key === 'ArrowDown' ||
					event.key === 'Enter'
				)) {
					event.preventDefault()
				}
				if(isMultiAddEnabled && event.key === 'Enter' && event.ctrlKey) {
					event.preventDefault()
					await tableRef.current.instance.saveEditData()
					tableRef.current.instance.addRow()
				}
			}}
			onToolbarPreparing={e => {
				e.toolbarOptions.items.push({
					widget: 'dxButton',
					location: 'after',
					options: {
						icon: 'arrowleft',
						onClick: () => history.push(sectionPath)
					}
				})
				onMultiEditEnabled && e.toolbarOptions.items.unshift({
					location: 'after',
					template: 'multiAdd'
				})
				e.toolbarOptions.items.unshift({
					location: 'after',
					template: 'import'
				})
				selectedRows && e.toolbarOptions.items.unshift({
					location: 'after',
					template: 'export'
				})
			}}
			onInitNewRow={handleAdd}
			templates={[MultiAddTemplate, ImportTemplate, ExportTemplate]}
		>
			<Editing
				mode='batch'
				allowUpdating={true}
				allowAdding={true}
			/>
		</StyledDxDataTable>
		</>
	)
}

export default GridView
